import { TextField } from "@mui/material";
import { styled } from "@mui/styles";
import { Field, Form, Formik } from "formik";
import { debounce } from "lodash";
import React from "react";
import ContainedButton from "src/components/common/ContainedButton";
import api from "src/store/features/api";
import Preloader from "src/utils/Preloader";
import { getReferralCodeEntryDisclosureMessage } from "src/utils/referral-partner-disclosure";
import * as Yup from "yup";

export const CodeWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  padding: "16px",
  paddingBottom: "32px",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "12px",
  alignSelf: "stretch",
  borderRadius: "16px",
  border: `1px solid ${theme.palette.secondary.contrastText}`,
  background: theme.palette.background.paper,
}));

export const ReferLinkHeader = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  fontSize: "16px",
  fontWeight: 600,
  textDecoration: "none",
  color: "inherit",
  cursor: "pointer",
  alignSelf: "stretch",
});

export const ReferText = styled("div")(({ theme }) => ({
  color: theme.palette.mode === "light" ? "#000" : "#FFF",
  fontSize: "12px",
}));

export const CodeField = styled(Field)(({ theme, issuccess }) => ({
  borderRadius: 16,
  color: "#8D9DA7", //text color
  fontSize: "16px",
  fontWeight: "500",
  marginTop: "7px",

  "& .MuiInputBase-input": {
    color: "#002035", // text input
    fontSize: "16px",
    padding: "16px",
    "&:-webkit-autofill": {
      transitionDelay: "9999s",
      transitionProperty: "background-color, color",
    },
  },
  "& .MuiFilledInput-root": {
    border: `1px solid #545454`,
    borderRadius: 16,
    backgroundColor: "#FFF",
    paddingTop: "8px",
    padingLeft: "8px",
    height: "54px",
  },
  "& .MuiFormLabel-root": {
    marginTop: "0px",
    fontWeight: 400, // Text input size
  },
  "& .MuiFormLabel-root.MuiInputLabel-shrink": {
    backgroundColor: "#fff",
    color: "#002035",
    padding: "2px",
    marginTop: "-18px",
  },
  "& .MuiFilledInput-root.Mui-focused": {
    border: "1px solid #002035", //border color
    borderRadius: 16,
  },
  "& label.Mui-focused": {
    color: "#8D9DA7",
  },
  "& .MuiFormHelperText-root.Mui-error": {
    fontSize: "14px",
    margin: "5px 0px",
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.error.main,
  },
  "& .MuiFormHelperText-root": {
    fontSize: "14px",
    margin: "5px 0px",
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.error.main,
  },
  "& .MuiFormLabel-root.Mui-error": {
    fontSize: "16px",
    fontWeight: theme.typography.fontWeightMedium,
    color: "#8D9DA7",
  },
  "& .MuiFilledInput-root.Mui-error": {
    border: `1px solid ${issuccess === "true" ? "green" : "#FC4C4C"}`, //border color
    fontSize: "16px",
    fontWeight: theme.typography.fontWeightMedium,
    color: "#8D9DA7",
  },
  "& .MuiFilledInput-root:hover": {
    border: "1px solid #002035", //border color
    borderRadius: 16,
  },
}));

const SuccessWrapper = styled("span")(({ theme }) => ({
  fontSize: "14px",
  margin: "5px 0px",
  fontWeight: theme.typography.fontWeightMedium,
  color: "green",
}));

const DisclosureWrapper = styled("div")(() => ({
  width: "100%",
  display: "flex",
  padding: "16px",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "8px",
  alignSelf: "center",
  marginBottom: "15px",
  borderRadius: "10px",
  backgroundColor: "#f7f2e9",
}));

const DisclosureText = styled("span")(() => ({
  color: "#7B858B",
  fontSize: "11px",
}));

const LoadingWrapper = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "24px",
});

export interface ReferralCodeEntryValues {
  code: string;
}

const ReferralCodeSchema = Yup.object().shape({
  code: Yup.string()
    .required("Referral code is required")
    .transform((value) => value?.toUpperCase()) // Convert to uppercase automatically.
    .trim(),
});

const initialValues: ReferralCodeEntryValues = {
  code: "",
};

const ReferralCodeEntry = () => {
  const [getPartnershipInfo] = api.useLazyGetReferalPartnerDetailsQuery();

  const [disclosureMsg, setDisclosureMsg] = React.useState<string>("");
  const [currentCode, setCurrentCode] = React.useState("");
  const prevCode = React.useRef<string>("");

  const [submitReferralCode, { isLoading, isSuccess, isError, error, reset }] =
    api.useSubmitReferralCodeMutation();

  const { data: referredBy } = api.useGetReferredByUserQuery();

  React.useEffect(() => {
    if (referredBy) {
      setCurrentCode(referredBy);
    }
  }, [referredBy]);

  const handleSubmit = async (values: ReferralCodeEntryValues) => {
    if (isLoading) return;
    const { code } = values;
    prevCode.current = code;
    await submitReferralCode({ referralCodeDto: { rpCode: code } });
  };

  React.useEffect(() => {
    // Create debounced function
    const debouncedFetch = debounce(async (code) => {
      try {
        const res = await getPartnershipInfo({ referrer: code }).unwrap();
        if (res) {
          setDisclosureMsg(getReferralCodeEntryDisclosureMessage(res.name, res.reward));
        }
      } catch {
        setDisclosureMsg("");
      }
    }, 500); // 500ms delay

    if (currentCode) {
      const referrerCode = currentCode.toLowerCase();
      debouncedFetch(referrerCode);
    }

    // Cleanup function to cancel any pending debounced calls
    return () => {
      debouncedFetch.cancel();
    };
  }, [currentCode]);

  React.useEffect(() => {
    if ((isSuccess || isError) && prevCode.current !== currentCode) {
      reset();
    }
  }, [prevCode.current, currentCode, isSuccess, isError]);

  const alreadyReferred = referredBy != null;

  return (
    <CodeWrapper>
      <ReferLinkHeader>Enter Referral Code</ReferLinkHeader>
      <ReferText>
        Enter your referral partner code here to be eligible for potential future giveaways and
        discounts
      </ReferText>
      <Formik
        initialValues={initialValues}
        validationSchema={ReferralCodeSchema}
        onSubmit={handleSubmit}>
        {({ errors, touched, setFieldValue }) => {
          return (
            <Form style={{ width: "100%", display: "flex", flexDirection: "column", gap: "24px" }}>
              <CodeField
                id="ReferralCodeEntry-input-field"
                as={TextField}
                onChange={(e: any) => {
                  setCurrentCode(e.target.value);
                  setFieldValue("code", e.target.value);
                }}
                InputProps={{
                  disableUnderline: true,
                }}
                value={currentCode}
                fullWidth
                name="code"
                type="text"
                label="Referral Code"
                error={(errors.code && touched.code) || isError}
                helperText={
                  (touched.code && errors.code) ||
                  (isError && error?.message) ||
                  (isSuccess && <SuccessWrapper>Successfully submitted</SuccessWrapper>) ||
                  (alreadyReferred &&
                    "You have already supplied a referral code. If you do not recognise this code, or believe this is an error, please contact our Support team.")
                }
                variant="filled"
                issuccess={String(isSuccess)}
                disabled={alreadyReferred}
              />

              {disclosureMsg && (
                <DisclosureWrapper>
                  <DisclosureText id="ReferralCodeEntry-disclosure">{disclosureMsg}</DisclosureText>
                </DisclosureWrapper>
              )}

              <ContainedButton
                id="ReferralCodeEntry-submit-button"
                type="submit"
                color="navy"
                disabled={!disclosureMsg || alreadyReferred}>
                {isLoading ? (
                  <LoadingWrapper>
                    Submitting
                    <Preloader size={"20px"} color="#FFF" />
                  </LoadingWrapper>
                ) : (
                  "Submit"
                )}
              </ContainedButton>
            </Form>
          );
        }}
      </Formik>
    </CodeWrapper>
  );
};

export default ReferralCodeEntry;
